import {
  AssetGroupStatistics,
  IntegrationStatistics,
  IntegrationType,
  useGetAssetGroupStatsQuery,
  useGetSpaceInventoryStatsQuery,
  useGetTotalAssetsQuery,
} from "~/operations";
import { ScopeType, SpaceOrWorkspaceScope, useScope } from "~/hooks/useScope";
import { Metrics } from "~/lib/arrow";

export type AssetGroupStatsItem = AssetGroupStatistics;
export type AssetGroupStats = AssetGroupStatsItem[];

export type IntegrationStatsItem = IntegrationStatistics;
export type IntegrationStats = IntegrationStatsItem[];

export type UseInventoryProps = {
  scope: SpaceOrWorkspaceScope;
  searchFilters?: string[];
};

export type Inventory = {
  assetGroupStats: AssetGroupStats;
  assetsTotalCount: number;
  workspaceAssetsTotalCount: number;
  spaceAssetsTotalCount: number;
  hasJiraIntegrationEnabled: boolean;
  refetch: () => void;
  refetchSpaceAssetsTotalCount: () => void;
  loading: boolean;
};

export function useInventory({
  scope,
  searchFilters = [],
}: UseInventoryProps): Inventory {
  const { spaceScope } = useScope();
  const spaceInventoryStats = useGetSpaceInventoryStatsQuery({
    variables: {
      spaceMrn: scope.mrn,
      metricMrns: [Metrics.AssetsRiskRatingDistribution],
    },
    skip: scope.type !== ScopeType.Space,
  });

  const { data: assetGroupStatsData, refetch: refetchAssetGroupStats } =
    useGetAssetGroupStatsQuery({
      variables: { scopeMrn: scope.mrn, queryTerms: searchFilters },
      skip: !scope.mrn,
    });

  const space =
    spaceInventoryStats?.data?.space?.__typename === "Space"
      ? spaceInventoryStats.data.space
      : undefined;

  const {
    data: workspaceAssetsTotalCountData,
    loading: workspaceAssetsTotalCountLoading,
  } = useGetTotalAssetsQuery({
    variables: {
      scopeMrn: scope.type === ScopeType.Workspace ? scope.mrn : undefined,
    },
    skip: scope.type !== ScopeType.Workspace,
    fetchPolicy: "network-only",
  });

  const {
    data: spaceAssetsTotalCountData,
    loading: spaceAssetsTotalCountLoading,
    refetch: refetchSpaceAssetsTotalCount,
  } = useGetTotalAssetsQuery({
    variables: { scopeMrn: spaceScope?.mrn },
    skip: scope.type !== ScopeType.Space,
    fetchPolicy: "network-only",
  });

  const workspaceAssetsTotalCount =
    workspaceAssetsTotalCountData?.assets?.totalCount ?? 0;

  const spaceAssetsTotalCount =
    spaceAssetsTotalCountData?.assets?.totalCount ?? 0;

  const refetch = () => {
    if (scope.type === ScopeType.Space) {
      spaceInventoryStats.refetch();
    }
  };

  function refetchQueries() {
    refetch();
    refetchAssetGroupStats();
  }

  // https://www.figma.com/file/ks8K3oC02Li9Xautp2jPeU/4.-Registry%2C-Security%2C-Vulnerabilities?node-id=60%3A22425
  // "Below, there can be up to 5 most important integrations (as discussed in the call:
  // Cloud providers first, so K8s, AWS, GCP – then OS & Containers; possibly based on
  // # of assets in each; not when there’s 0 of a given type of course)"
  const sortByImportant = (a: AssetGroupStatsItem, b: AssetGroupStatsItem) => {
    const cloudGroup = ["k8s", "aws", "gcp", "azure", "vmware", "oci"];
    const osContainerGroup = ["os", "container"];
    const supplyChainGroup = ["terraform"];
    const saasGroup = [
      "ms365",
      "okta",
      "google-workspace",
      "saas_services",
      "gitlab",
      "github",
      "slack",
    ];
    const networkGroup = ["network_devices", "domains_and_hosts"];

    // least important first
    const groupsByImportance = [
      networkGroup,
      saasGroup,
      supplyChainGroup,
      osContainerGroup,
      cloudGroup,
    ];

    const aIdx = groupsByImportance.findIndex((g) => g.includes(a.groupType));
    const bIdx = groupsByImportance.findIndex((g) => g.includes(b.groupType));

    if (aIdx === bIdx) {
      return b.count - a.count;
    }

    return bIdx - aIdx;
  };

  const assetGroupStats = (assetGroupStatsData?.assets?.assetGroupStats || [])
    .filter((g) => g.count > 0)
    .sort(sortByImportant);

  const integrationStats = space?.stats?.integrationsStatistics;
  const hasJiraIntegrationEnabled =
    integrationStats?.some(
      (ai) => ai.total > 0 && ai.type === IntegrationType.TicketSystemJira,
    ) || false;

  return {
    assetGroupStats,
    assetsTotalCount:
      scope.type === ScopeType.Workspace
        ? workspaceAssetsTotalCount
        : spaceAssetsTotalCount,
    workspaceAssetsTotalCount,
    spaceAssetsTotalCount,
    hasJiraIntegrationEnabled,
    refetch: refetchQueries,
    refetchSpaceAssetsTotalCount,
    loading:
      spaceInventoryStats.loading ||
      workspaceAssetsTotalCountLoading ||
      spaceAssetsTotalCountLoading,
  };
}
